import { createFileRoute, Link } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { GuestOnlyGuard } from '@/components/auth-guard';
import { RequestResetForm } from '@/features/auth/components/request-reset-form';
import { ResetPasswordForm } from '@/features/auth/components/reset-password-form';
import { AuthCard } from '@/features/auth/views';

const SearchSchema = z.object({
  token: z.string().min(1).optional(),
});

const STRONG_LINK_STYLE = {
  color: 'var(--vq-ink)',
  textDecoration: 'none',
  fontWeight: 500,
} as const;

function ResetPasswordRoute(): ReactNode {
  const { t } = useTranslation('auth');
  const { token } = Route.useSearch();

  // Two distinct flows behind one route:
  //   • no token → "send me a reset email" form
  //   • token in querystring → "choose a new password" form
  if (token === undefined || token === '') {
    return (
      <GuestOnlyGuard>
        <AuthCard
          title={t('requestReset.title')}
          subtitle={t('requestReset.subtitle')}
          footer={
            <Link to="/login" style={STRONG_LINK_STYLE} className="hover:underline">
              {t('requestReset.backToLogin')}
            </Link>
          }
        >
          <RequestResetForm />
        </AuthCard>
      </GuestOnlyGuard>
    );
  }

  return (
    <GuestOnlyGuard>
      <AuthCard
        title={t('resetPassword.title')}
        subtitle={t('resetPassword.subtitle')}
        footer={
          <Link to="/login" style={STRONG_LINK_STYLE} className="hover:underline">
            {t('resetPassword.backToLogin')}
          </Link>
        }
      >
        <ResetPasswordForm token={token} />
      </AuthCard>
    </GuestOnlyGuard>
  );
}

export const Route = createFileRoute('/reset-password')({
  validateSearch: (search) => SearchSchema.parse(search),
  component: ResetPasswordRoute,
});
